import { Component } from 'react';
import { connect } from 'react-redux';
import { userInfoSync } from 'hub-http/userInfo';
import unescapedText from 'I18n/utils/unescapedText';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { H2 } from 'foundations-components/transitional/components/H2';
import { ModalPanel } from 'foundations-components/transitional/components/ModalPanel';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { Form } from 'foundations-components/transitional/components/Form';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { Select } from 'foundations-components/transitional/components/Select';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import Routes from 'ContentUtils/Routes';
import { saveEmail as saveEmailAction } from 'ContentData/actions/Emails';
import { fetchEmail } from 'ContentData/api/Emails';
import { getBusinessUnitId } from 'ContentData/selectors/branding';
import FormsContext from 'ContentComponents/context/FormsContext';
import EmailAddressSelect from 'ContentComponents/components/cmv2/form/formAutomation/followupEmail/emailEditor/EmailAddressSelect';
import FooterSelect from 'ContentComponents/components/cmv2/form/formAutomation/followupEmail/emailEditor/FooterSelect';
import SubjectField from 'ContentComponents/components/cmv2/form/formAutomation/followupEmail/emailEditor/SubjectField';
import * as RequestStatuses from 'ContentUtils/constants/RequestStatus';
import { Link } from 'foundations-components/transitional/components/Link';
import { View } from 'foundations-components/transitional/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var EmailSavingErrorTypes;
(function (EmailSavingErrorTypes) {
  EmailSavingErrorTypes["CONTENT_SCOPE_RESTRICTED"] = "CONTENT_SCOPE_RESTRICTED";
  EmailSavingErrorTypes["REPLY_TO_UNVERIFIED_EMAIL"] = "REPLY_TO_UNVERIFIED_EMAIL";
  EmailSavingErrorTypes["VALIDATION_FAILED"] = "VALIDATION_FAILED";
})(EmailSavingErrorTypes || (EmailSavingErrorTypes = {}));
const KEYS_REQUIRED_TO_CREATE = ['name', 'fromName', 'replyTo', 'subject', 'primaryRichTextModuleHtml', 'subscription', 'canSpamSettingsId'];
function mapStateToProps(state, props) {
  return {
    businessUnitId: getBusinessUnitId(state, props)
  };
}
const mapDispatchToProps = {
  saveEmail: saveEmailAction
};
const connector = connect(mapStateToProps, mapDispatchToProps);
const i18nKey = key => `ContentComponents.cmv2.fields.form.formWorkflowDependencies.followupEmailWorkflows.emailEditorPanel.${key}`;
class EmailEditorPanel extends Component {
  constructor(props) {
    super(props);
    this.updateEmail = this.updateEmail.bind(this);
    this.onEmailSave = this.onEmailSave.bind(this);
    this.state = {
      email: null,
      emailSavingStatus: RequestStatuses.UNINITIALIZED,
      showEmailFetchError: false,
      showUnverifiedEmailError: false
    };
  }
  componentDidMount() {
    const {
      emailId
    } = this.props;
    if (emailId) {
      // Must fetch email even if it's already in state because we need ALL the email data
      // and automated emails fetch only returns a subset of the properties we need
      fetchEmail(emailId).then(emailResponse => {
        this.setState({
          email: emailResponse
        });
      }).catch(() => {
        this.setState({
          showEmailFetchError: true
        });
      });
    }
  }
  updateEmail(prop, newValue) {
    const {
      email
    } = this.state;
    this.setState({
      email: Object.assign({}, email, {
        [prop]: newValue
      })
    });
  }
  onEmailSave() {
    const {
      emailId,
      onClose,
      saveEmail
    } = this.props;
    const {
      email
    } = this.state;
    if (!email) return;
    this.setState({
      emailSavingStatus: RequestStatuses.PENDING
    });
    const emailData = KEYS_REQUIRED_TO_CREATE.reduce((acc, key) => {
      acc[key] = email[key];
      return acc;
    }, {});
    if (!emailId && this.hasBusinessUnitsAccess) {
      emailData.businessUnitId = this.businessUnitId;
    }
    saveEmail(emailData, emailId).then(response => {
      this.setState({
        emailSavingStatus: RequestStatuses.SUCCEEDED
      });
      onClose(emailId || response.id);
    }).catch(error => {
      const {
        errorType,
        errors
      } = error.responseJSON || {};
      if (errorType === EmailSavingErrorTypes.CONTENT_SCOPE_RESTRICTED) {
        this.setState({
          emailSavingStatus: RequestStatuses.FAILED
        });
        FloatingAlertStore.addAlert({
          titleText: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('noPublishAccessError.title')
          }),
          message: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('noPublishAccessError.message')
          }),
          type: 'danger'
        });
      } else if (errorType === EmailSavingErrorTypes.VALIDATION_FAILED && !!errors.some(err => err.errorType === EmailSavingErrorTypes.REPLY_TO_UNVERIFIED_EMAIL)) {
        this.setState({
          emailSavingStatus: RequestStatuses.FAILED,
          showUnverifiedEmailError: true
        });
      } else {
        this.setState({
          emailSavingStatus: RequestStatuses.FAILED
        });
      }
    });
  }
  isSaveDisabled() {
    const {
      email
    } = this.state;
    return !email || KEYS_REQUIRED_TO_CREATE.some(key => {
      return !email[key];
    });
  }
  get scopes() {
    if (!this._scopes) {
      const {
        user
      } = userInfoSync();
      this._scopes = user.scopes;
    }
    return this._scopes;
  }
  get hasBusinessUnitsAccess() {
    return Routes.isUngated('campaign-business-unit') && this.scopes.includes('business-units-access');
  }
  get businessUnitId() {
    const {
      businessUnitId
    } = this.props;
    const {
      email
    } = this.state;
    return email && Number.isInteger(email.businessUnitId) ? email.businessUnitId : businessUnitId || 0;
  }
  renderSubscriptionTypeSelect() {
    const {
      emailId,
      pickerComponents
    } = this.props;
    const {
      SubscriptionTypeSelect
    } = pickerComponents;
    const {
      email
    } = this.state;
    const {
      subscription
    } = email || {};
    const isEditingTransactionalEmail = emailId && subscription === -1;
    if (isEditingTransactionalEmail || !SubscriptionTypeSelect) {
      return null;
    }
    const customFilter = _subscription => {
      if (this.hasBusinessUnitsAccess) {
        return _subscription.businessUnitId === this.businessUnitId;
      }
      return true;
    };
    if (Routes.isUngated('subscriptions:groupSettings')) {
      return /*#__PURE__*/_jsx(SubscriptionTypeSelect, {
        defaultTypesText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('subscription.defaultTypesText')
        }),
        includeInternal: false,
        isUngatedForSubscriptionGroups: true,
        onChange: type => this.updateEmail('subscription', type ? type.id : null),
        onGroupChange: e => this.updateEmail('preferencesGroupId', e.target.value),
        renderGroupSelect: ({
          onChange,
          options,
          value
        }) => /*#__PURE__*/_jsx(FormControl, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('preferencesGroupId.label')
          }),
          required: true,
          children: /*#__PURE__*/_jsx(Select, {
            placeholder: unescapedText(i18nKey('preferencesGroupId.placeholder')),
            onChange: onChange,
            options: options,
            value: value
          })
        }),
        renderTypeSelect: ({
          onChange,
          options,
          value
        }) => /*#__PURE__*/_jsx(FormControl, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('subscription.label')
          }),
          required: true,
          children: /*#__PURE__*/_jsx(Select, {
            placeholder: unescapedText(i18nKey('subscription.placeholder')),
            onChange: onChange,
            options: options,
            value: value
          })
        }),
        value: subscription
      });
    }
    return /*#__PURE__*/_jsx(SubscriptionTypeSelect, {
      customFilter: customFilter,
      customFooter: /*#__PURE__*/_jsx(Link, {
        href: Routes.settings('email', 'types'),
        external: true,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('subscription.dropdownFooterLink')
        })
      }),
      formControlProps: {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('subscription.label')
        }),
        required: true
      },
      hideInternal: true,
      isUngatedForSubscriptionGroups: false,
      placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('subscription.placeholder')
      }),
      onChange: type => this.updateEmail('subscription', type ? type.id : null),
      value: subscription
    });
  }
  renderEmailEditorLink() {
    const {
      emailId,
      hasAutomatedEmailEditorAccess
    } = this.props;
    if (!hasAutomatedEmailEditorAccess) {
      return null;
    }
    let emailEditUrl;
    if (emailId) {
      emailEditUrl = Routes.editEmail(emailId);
    } else {
      emailEditUrl = Routes.emailContentCreator({
        emailType: 'AUTOMATED',
        businessUnitId: this.hasBusinessUnitsAccess ? this.businessUnitId : null
      });
    }
    return /*#__PURE__*/_jsx("p", {
      children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: i18nKey('helpText_jsx'),
        options: {
          external: true,
          href: emailEditUrl,
          style: {
            whiteSpace: 'nowrap'
          }
        },
        elements: {
          Link
        }
      })
    });
  }
  renderEmailEditForm() {
    const {
      emailId,
      pickerComponents
    } = this.props;
    const {
      email,
      showUnverifiedEmailError
    } = this.state;
    const {
      canSpamSettingsId,
      name,
      fromName,
      primaryRichTextModuleHtml,
      replyTo,
      subject
    } = email || {};
    return /*#__PURE__*/_jsx(View, {
      paddingBlockEnd: "1000",
      children: /*#__PURE__*/_jsxs(Form, {
        children: [this.renderEmailEditorLink(), /*#__PURE__*/_jsx(FormControl, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('emailName.label')
          }),
          required: true,
          children: /*#__PURE__*/_jsx(TextInput, {
            onChange: e => this.updateEmail('name', e.target.value),
            value: name || ''
          })
        }), /*#__PURE__*/_jsx(FormControl, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('fromName.label')
          }),
          required: true,
          children: /*#__PURE__*/_jsx(TextInput, {
            onChange: e => this.updateEmail('fromName', e.target.value),
            value: fromName || ''
          })
        }), /*#__PURE__*/_jsx(EmailAddressSelect, {
          showUnverifiedEmailError: showUnverifiedEmailError,
          onChange: newAddress => this.updateEmail('replyTo', newAddress),
          value: replyTo
        }), this.renderSubscriptionTypeSelect(), /*#__PURE__*/_jsx(SubjectField, {
          pickerComponents: pickerComponents,
          value: subject || '',
          onChange: value => this.updateEmail('subject', value)
        }), /*#__PURE__*/_jsx(FormControl, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('primaryRichTextModuleHtml.label')
          }),
          required: true,
          children: /*#__PURE__*/_jsx(FormsContext.Consumer, {
            children: ({
              RichTextComponent
            }) => RichTextComponent && /*#__PURE__*/_jsx(RichTextComponent, {
              id: emailId ? email && email.id : null // if editing an existing email, this will force a re-render when the email data loads
              ,
              isFormFollowupEmail: true,
              onFieldChange: newEmailBody => this.updateEmail('primaryRichTextModuleHtml', newEmailBody),
              value: primaryRichTextModuleHtml
            })
          })
        }), /*#__PURE__*/_jsx(FooterSelect, {
          onChange: newFooterId => this.updateEmail('canSpamSettingsId', newFooterId),
          value: canSpamSettingsId
        })]
      })
    });
  }

  // TODO: add a delete email button?
  render() {
    const {
      emailId,
      onClose
    } = this.props;
    const {
      email,
      emailSavingStatus,
      showEmailFetchError
    } = this.state;
    const isSaveDisabled = this.isSaveDisabled();
    let content;
    if (showEmailFetchError) {
      content = /*#__PURE__*/_jsx(Alert, {
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('emailFetchError.title')
        }),
        type: "danger",
        marginBlockEnd: "600",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('emailFetchError.message')
        })
      });
    } else if (emailId && !email) {
      content = /*#__PURE__*/_jsx(LoadingSpinner, {
        layout: "centered",
        grow: true
      });
    } else {
      content = this.renderEmailEditForm();
    }
    return /*#__PURE__*/_jsxs(ModalPanel, {
      width: 460,
      children: [/*#__PURE__*/_jsxs(DialogHeader, {
        children: [/*#__PURE__*/_jsx(H2, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('header')
          })
        }), /*#__PURE__*/_jsx(DialogCloseButton, {
          onClick: () => onClose()
        })]
      }), /*#__PURE__*/_jsx(DialogBody, {
        children: content
      }), /*#__PURE__*/_jsx(DialogFooter, {
        children: /*#__PURE__*/_jsx(Tooltip, {
          placement: "top",
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('saveButton.disabledTooltip')
          }),
          disabled: !isSaveDisabled,
          children: /*#__PURE__*/_jsx(LoadingButton, {
            disabled: isSaveDisabled,
            size: "small",
            use: "tertiary",
            keepResultAfterFinish: true,
            responsive: false,
            onClick: this.onEmailSave,
            loading: emailSavingStatus === RequestStatuses.PENDING,
            failed: emailSavingStatus === RequestStatuses.FAILED,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: i18nKey('saveButton.label')
            })
          })
        })
      })]
    });
  }
}
export default connector(EmailEditorPanel);